import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
    ReactNode,
    ChangeEvent,
    KeyboardEvent,
    isValidElement,
    cloneElement,
} from "react";
import OutsideClickHandler from "react-outside-click-handler";
import Input from "../TextInput";
import OptionList from "../SelectInput/Select/OptionList";
import { getClassNames, transformOptions } from "../../utils";
import uid from "../../utils/uid";
import withTheme from "../../hocs/withTheme";

import "./AutoComplete.css";

// Define a concrete type for the options
interface AutoCompleteOption {
    value: string | number;
    label: string;
}

type AutoCompleteProps = {
    id?: string;
    value?: string;
    dataSource?: (string | AutoCompleteOption)[];
    placeholder?: string;
    onSelect?: (val: string) => void;
    onSearch?: (val: string) => void;
    className?: string;
    dropdownClassName?: string;
    isDropdownOpen?: boolean;
    children?: ReactNode;
    externalSearch?: boolean;
};

function AutoComplete(props: AutoCompleteProps) {
    const {
        id = null,
        value: valueProp = undefined,
        dataSource = [],
        placeholder = "Start typing...",
        children = null,
        className: classNameProp = undefined,
        dropdownClassName: dropdownClassNameProp = undefined,
        onSearch = () => {},
        onSelect = () => {},
        isDropdownOpen = false,
        externalSearch = false,
    } = props;

    // Fixed: Added HTMLDivElement type to Ref
    const listRef = useRef<HTMLDivElement>(null);
    const uId = useMemo(() => id || uid("results"), [id]);

    // Fixed: Explicitly typed states
    const [data, setData] = useState<AutoCompleteOption[]>(transformOptions(dataSource));
    const [value, setValue] = useState<string | number>(valueProp || "");
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [opened, setOpened] = useState<boolean>(isDropdownOpen);
    const [options, setOptions] = useState<AutoCompleteOption[]>([]);

    const updateOptions = useCallback(
        (val: string | number = value) => {
            if (!externalSearch) {
                const filtered = data.filter((item) =>
                    `${item.value}`
                        .toLocaleLowerCase()
                        .includes(String(val).toLocaleLowerCase())
                );
                setOptions(filtered);
            } else {
                setOptions(data);
            }
        },
        [data, value, externalSearch]
    );

    useEffect(() => {
        setData(transformOptions(dataSource));
    }, [dataSource]);

    useEffect(() => {
        setValue(valueProp || "");
    }, [valueProp]);

    // Scroll logic with fixed type safety
    useEffect(() => {
        if (listRef.current && activeIndex !== -1) {
            const list = listRef.current;
            const { scrollTop, offsetHeight: height } = list;
            const scrollBottom = scrollTop + height;
            const item = list.children[activeIndex] as HTMLElement | undefined;

            if (item) {
                const heightItem = item.offsetHeight;
                const topItem = item.offsetTop;

                if (topItem > scrollBottom - heightItem) {
                    list.scrollTo({
                        top: topItem - scrollBottom + scrollTop + heightItem,
                    });
                } else if (topItem < scrollTop) {
                    list.scrollTo({ top: topItem });
                }
            }
        }
    }, [activeIndex]);

    useEffect(() => {
        setOpened(isDropdownOpen);
    }, [isDropdownOpen]);

    useEffect(() => {
        updateOptions();
    }, [updateOptions]);

    const closeDropdown = () => {
        setOpened(false);
        setActiveIndex(-1);
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setValue(val);
        onSearch(val);
        setOpened(true);
        updateOptions(val);
    };

    const onFocus = () => {
        setOpened(true);
        updateOptions();
    };

    const selectItem = (selection: string | AutoCompleteOption) => {
        const selectedValue = typeof selection === "string" ? selection : selection?.value;

        setValue(valueProp !== undefined ? valueProp : selectedValue);
        onSelect(selectedValue as string);
        closeDropdown();
    };

    const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
        // Safe check for child props
        if (isValidElement(children) && typeof (children.props as any).onKeyUp === "function") {
            (children.props as any).onKeyUp(e);
        }

        switch (e.key) {
            case "ArrowUp":
            case "ArrowDown":
            case "Escape":
            case "Enter":
                e.preventDefault();
                return;
            default:
                updateOptions();
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (isValidElement(children) && typeof (children.props as any).onKeyDown === "function") {
            (children.props as any).onKeyDown(e);
        }

        if (e.key === "Escape") {
            closeDropdown();
            setValue("");
            return;
        }

        if (options.length < 1) return;

        switch (e.key) {
            case "ArrowUp":
                if (activeIndex > 0) setActiveIndex(activeIndex - 1);
                break;
            case "ArrowDown":
                if (activeIndex < options.length - 1) setActiveIndex(activeIndex + 1);
                break;
            case "Enter":
                if (activeIndex >= 0) selectItem(options[activeIndex]);
                return;
            case "Tab":
                if (activeIndex >= 0) selectItem(options[activeIndex]);
                closeDropdown();
                return;
            default:
                return;
        }
        e.preventDefault();
    };

    const selected = useMemo(
        () => options[activeIndex]?.value || "",
        [options, activeIndex]
    );

    const inputProps = useMemo(
        () => ({
            value,
            placeholder,
            role: "combobox",
            autoComplete: "off",
            "aria-owns": uId,
            "aria-controls": uId,
            "aria-expanded": opened,
            "aria-describedby": "initInstr",
            "aria-autocomplete": "both" as const,
            "aria-activedescendant": selected,
            onFocus,
            onKeyUp,
            onKeyDown,
            onChange,
        }),
        [uId, placeholder, value, opened, selected, onChange, onFocus]
    );

    const child = useMemo(
        () => (isValidElement(children) ? cloneElement(children as React.ReactElement, inputProps) : null),
        [children, inputProps]
    );

    const className = getClassNames(classNameProp, "AutoComplete", {
        AutoComplete__opened: opened && options.length > 0,
    });

    const dropdownClassName = getClassNames(
        dropdownClassNameProp,
        "AutoComplete-dropdown",
        { "AutoComplete-dropdown__opened": opened && options.length > 0 }
    );

    return (
        <OutsideClickHandler onOutsideClick={closeDropdown}>
            <div className={className}>
                {child || <Input {...inputProps} />}
                <div
                    id={uId}
                    className={dropdownClassName}
                    role="listbox"
                    ref={listRef}
                >
                    <OptionList
                        name={`${uId}-option`}
                        options={options}
                        selected={selected as string}
                        clicked={selectItem}
                    />
                </div>
            </div>
        </OutsideClickHandler>
    );
}

export default withTheme(AutoComplete);